<script>
import usersApi from "@/services/users";
import BIcon from "@/components/BIcon.vue";
import GoogleIcon from "@/components/Icons/GoogleIcon.vue";

export default {
  name: "CustomerAuth",

  components: {
    BIcon,
    GoogleIcon
  },

  data() {
    return {
      formMode: "login",
      login: {
        email: "",
        password: ""
      },
      signup: {
        email: "",
        name: "",
        password: "",
        password2: ""
      },
      passwordRules: {
        isMinLengthMet: false,
        containsNumber: false,
        containsUpperAndLower: false,
        isValid: false
      },
      loginErrors: {
        email: "",
        password: ""
      },
      signupErrors: {
        name: "",
        email: "",
        password: "",
        password2: ""
      },
      loading: false,
      preLoading: true
    };
  },

  computed: {
    isValidLoginEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.login.email);
    },
    isValidSignupEmail() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.signup.email);
    },
    redirect() {
      return this.$route.query?.redirect;
    },
    websiteId() {
      return this.$route.query?.websiteId;
    },
    isLogout() {
      return !!this.$route.query?.logout;
    }
  },

  created() {
    if (!this.redirect || !this.websiteId) {
      this.$router.replace("/");
      return;
    }
  },

  mounted() {
    this.loadGoogleScript();

    if (this.$route.query.retry) {
      this.$notifyError(this.$t("login_error"));
    }
  },

  methods: {
    async handleCredentialResponse(response) {
      this.loading = true;
      usersApi
        .loginFinalCustomer({
          credential: response.credential,
          state: this.websiteId
        })
        .then(({ data: { redirect } }) => this.redirectAuthenticated(redirect))
        .catch(e => {
          this.$notifyError(e?.response?.data || this.$t("request_error"));
          this.loading = false;
        });
    },
    loadGoogleScript() {
      const gClientId = process.env.VUE_APP_GOOGLE_FINAL_CLIENT_ID;
      window.google.accounts.id.initialize({
        client_id: gClientId,
        callback: this.handleCredentialResponse
      });
      window.google.accounts.id.renderButton(this.$refs.googleLoginBtn, {
        text: "continue_with",
        size: "large",
        width: "378",
        theme: "filled_black",
        logo_alignment: "left"
      });
    },
    backToSite() {
      if (process.env.NODE_ENV === "development") {
        window.location.href = "http://localhost:3000";
      } else {
        window.location.href = this.redirect;
      }
    },
    async redirectAuthenticated(redirect) {
      window.location.href = redirect;
    },
    validatePassword() {
      const password = this.signup.password;

      this.passwordRules.isMinLengthMet = password.length >= 8;
      this.passwordRules.containsNumber = /\d/.test(password);
      this.passwordRules.containsUpperAndLower =
        /[a-z]/.test(password) && /[A-Z]/.test(password);

      this.validateSignupField("password");
    },
    validateSignupField(field) {
      if (this.signupErrors[field]) {
        this.signupErrors[field] = this.isSignupFieldValid(field);
      }
    },
    validateLoginField(field) {
      if (this.loginErrors[field]) {
        this.loginErrors[field] = this.isLoginFieldValid(field);
      }
    },
    isLoginFieldValid(field) {
      switch (field) {
        case "email": {
          if (!this.login.email) {
            return this.$t("final_customer_login.errors.required");
          }
          if (!this.isValidLoginEmail) {
            return this.$t("final_customer_login.errors.invalid_email");
          }
          return "";
        }
        case "password": {
          if (!this.login.password) {
            return this.$t("final_customer_login.errors.required");
          }
          return "";
        }
        default:
          return "";
      }
    },
    isSignupFieldValid(field) {
      switch (field) {
        case "name": {
          if (!this.signup.name) {
            return this.$t("final_customer_login.errors.required");
          }
          return "";
        }
        case "email": {
          if (!this.signup.email) {
            return this.$t("final_customer_login.errors.required");
          }
          if (!this.isValidSignupEmail) {
            return this.$t("final_customer_login.errors.invalid_email");
          }
          return "";
        }
        case "password": {
          if (!this.signup.password) {
            return this.$t("final_customer_login.errors.required");
          }
          if (!this.passwordRules.isMinLengthMet) {
            return this.$t("final_customer_login.errors.min_length").replace(
              /{{min}}/,
              8
            );
          }
          if (!this.passwordRules.containsNumber) {
            return this.$t("final_customer_login.errors.contains_number");
          }
          if (!this.passwordRules.containsUpperAndLower) {
            return this.$t("final_customer_login.errors.contains_lower_upper");
          }
          return "";
        }
        case "password2": {
          if (!this.signup.password2) {
            return this.$t("final_customer_login.errors.required");
          }
          if (this.signup.password !== this.signup.password2) {
            return this.$t("final_customer_login.errors.password_mismatch");
          }
          return "";
        }
        default:
          return "";
      }
    },
    changeFormMode() {
      this.login = {
        email: "",
        password: ""
      };
      this.signup = {
        email: "",
        name: "",
        password: "",
        password2: ""
      };
      this.passwordRules = {
        isMinLengthMet: false,
        containsNumber: false,
        containsUpperAndLower: false,
        isValid: false
      };
      this.loginErrors = {
        email: "",
        password: ""
      };
      this.signupErrors = {
        name: "",
        email: "",
        password: "",
        password2: ""
      };
      if (this.formMode === "login") this.formMode = "signup";
      else this.formMode = "login";
    },
    handleLogin() {
      Object.keys(this.loginErrors).forEach(field => {
        this.loginErrors[field] = this.isLoginFieldValid(field);
      });

      if (Object.values(this.loginErrors).some(error => !!error)) {
        return;
      }

      this.loading = true;
      usersApi
        .loginFinalCustomer({
          password: this.login.password,
          email: this.login.email,
          state: this.websiteId
        })
        .then(({ data: { redirect } }) => this.redirectAuthenticated(redirect))
        .catch(e => {
          this.$notifyError(e?.response?.data || this.$t("request_error"));
          this.loading = false;
        });
    },
    handleSignup() {
      Object.keys(this.signupErrors).forEach(field => {
        this.signupErrors[field] = this.isSignupFieldValid(field);
      });

      if (Object.values(this.signupErrors).some(error => !!error)) {
        return;
      }

      this.loading = true;
      usersApi
        .signupFinalCustomer({
          name: this.signup.name,
          password: this.signup.password,
          email: this.signup.email,
          state: this.websiteId
        })
        .then(({ data: { redirect } }) => this.redirectAuthenticated(redirect))
        .catch(e => {
          this.$notifyError(e?.response?.data || this.$t("request_error"));
          this.loading = false;
        });
    },
    submit(e) {
      e?.preventDefault();
      if (this.formMode === "login") {
        this.handleLogin();
      } else {
        this.handleSignup();
      }
    },
    handleCustomGoogleBtnClick() {
      const element = this.$refs.googleLoginBtn?.querySelector(
        "div[role=button]"
      );
      element.click();
    }
  }
};
</script>

<template>
  <div class="main-container h-dv-100 flex flex-col">
    <div>
      <!-- Toolbar -->
      <div class="flex justify-between items-center">
        <button
          class="flex gap-2 justify-center items-center"
          @click="backToSite"
        >
          <div class="p-1 self-center flex bg-gray-300 rounded-full">
            <b-icon name="arrow-left" class="self-center" />
          </div>
          <span class="font-semibold text-xl">{{
            $t("final_customer_login.back_to_site")
          }}</span>
        </button>
        <img src="@/assets/logo.png" alt="Biew" class="logo" />
      </div>

      <!-- Container -->
      <div class="flex justify-center items-center p-10 flex-1 h-full">
        <form class="form" @submit.prevent="submit">
          <b-loading
            :active.sync="loading"
            :is-full-page="true"
            :can-cancel="false"
          />

          <div class="mb-6">
            <h6 class="title">
              {{
                formMode === "login"
                  ? $t("final_customer_login.login")
                  : $t("final_customer_login.sign_me_up")
              }}
            </h6>
            <div class="flex justify-center content-center gap-2">
              <p class="title small">
                {{
                  formMode === "login"
                    ? $t("final_customer_login.with")
                    : $t("final_customer_login.via")
                }}
              </p>
              <img src="@/assets/logo.png" alt="Biew" class="title image" />
            </div>
          </div>

          <!-- Login Form -->
          <section v-show="formMode === 'login'" class="flex flex-col gap-4">
            <b-field
              :label="$t('final_customer_login.form.email.label')"
              :type="loginErrors.email ? 'is-danger' : ''"
              :message="loginErrors.email"
            >
              <b-input
                v-model="login.email"
                :placeholder="$t('final_customer_login.form.email.placeholder')"
                @input="validateLoginField('email')"
              >
              </b-input>
            </b-field>

            <b-field
              :label="$t('final_customer_login.form.password.label')"
              :type="loginErrors.password ? 'is-danger' : ''"
              :message="loginErrors.password"
            >
              <b-input
                v-model="login.password"
                type="password"
                password-reveal
                :placeholder="
                  $t('final_customer_login.form.password.placeholder')
                "
                @input="validateLoginField('password')"
              >
              </b-input>
            </b-field>

            <a role="button" class="link mb-4" style="margin-top: -12px">
              {{ $t("final_customer_login.forgot_password") }}
            </a>

            <b-button
              class="w-full btn-submit"
              type="is-primary"
              native-type="submit"
              :disabled="loading"
              >{{ $t("final_customer_login.login") }}</b-button
            >
          </section>

          <!-- Signup Form -->
          <section v-show="formMode === 'signup'" class="flex flex-col gap-3">
            <b-field
              :label="$t('final_customer_login.form.name.label')"
              :type="signupErrors.name ? 'is-danger' : ''"
              :message="signupErrors.name"
            >
              <b-input
                v-model="signup.name"
                :placeholder="$t('final_customer_login.form.name.placeholder')"
                @input="validateSignupField('name')"
              ></b-input>
            </b-field>

            <b-field
              :label="$t('final_customer_login.form.email.label')"
              :type="signupErrors.email ? 'is-danger' : ''"
              :message="signupErrors.email"
            >
              <b-input
                v-model="signup.email"
                :placeholder="$t('final_customer_login.form.email.placeholder')"
                @input="validateSignupField('email')"
              ></b-input>
            </b-field>

            <b-field
              :label="$t('final_customer_login.form.password.label')"
              :type="signupErrors.password ? 'is-danger' : ''"
              :message="signupErrors.password"
            >
              <b-input
                v-model="signup.password"
                :placeholder="
                  $t('final_customer_login.form.password.placeholder')
                "
                type="password"
                password-reveal
                @input="validatePassword"
              ></b-input>
            </b-field>

            <b-field
              :label="$t('final_customer_login.form.confirm_password.label')"
              :type="signupErrors.password2 ? 'is-danger' : ''"
              :message="signupErrors.password2"
            >
              <b-input
                v-model="signup.password2"
                :placeholder="
                  $t('final_customer_login.form.confirm_password.placeholder')
                "
                type="password"
                password-reveal
                @input="validateSignupField('password2')"
              ></b-input>
            </b-field>

            <section>
              <b>{{ $t("final_customer_login.password_must") }}</b>
              <ul class="rules-list">
                <li
                  :class="{
                    'text-red-600':
                      !passwordRules.containsUpperAndLower && signup.password
                  }"
                >
                  {{
                    $t(
                      "final_customer_login.password_rules.contains_lower_upper"
                    )
                  }}
                </li>
                <li
                  :class="{
                    'text-red-600':
                      !passwordRules.containsNumber && signup.password
                  }"
                >
                  {{
                    $t("final_customer_login.password_rules.contains_number")
                  }}
                </li>
                <li
                  :class="{
                    'text-red-600':
                      !passwordRules.isMinLengthMet && signup.password
                  }"
                >
                  {{
                    $t(
                      "final_customer_login.password_rules.min_length"
                    ).replace("{min}", 8)
                  }}
                </li>
              </ul>
            </section>

            <b-button
              class="w-full mt-5 btn-submit"
              type="is-primary"
              native-type="submit"
              :disabled="loading"
              >{{ $t("final_customer_login.sign_me_up") }}
            </b-button>
          </section>

          <!-- Form handler -->
          <div>
            <div class="divider">
              <div><span></span></div>
              <span>{{ $t("final_customer_login.or") }}</span>
              <div><span></span></div>
            </div>

            <div class="flex flex-col justify-center items-center">
              <button
                class="w-full btn-google"
                type="button"
                :disabled="loading"
                @click="handleCustomGoogleBtnClick"
              >
                <google-icon />
                <span>{{
                  $t("final_customer_login.continue_with_google")
                }}</span>
              </button>
              <div ref="googleLoginBtn" class="hidden"></div>
            </div>
          </div>

          <section
            class="w-full flex flex-col justify-center items-center mt-10 gap-0"
          >
            <span class="small-text">
              {{
                formMode === "login"
                  ? $t("final_customer_login.dont_have_an_account")
                  : $t("final_customer_login.already_have_an_account")
              }}
            </span>
            <a role="button" class="link small" @click="changeFormMode">
              {{
                formMode === "login"
                  ? $t("final_customer_login.sign_me_up")
                  : $t("final_customer_login.login")
              }}
            </a>
          </section>
        </form>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-container {
  padding: 50px;
}
.logo {
  width: 136px;
  height: 40px;
}
.title {
  text-align: center;
  font-weight: 600;
  font-size: 1.8rem;
  letter-spacing: 0.2px;

  &.small {
    text-align: center;
    font-weight: 400;
    font-size: 1rem;
    letter-spacing: 0.4px;
  }

  &.image {
    width: 68px;
    height: 20px;
  }
}
.h-dv-100 {
  height: 100dvh;
}
.form {
  width: 480px;
  min-height: 500px;
  padding: 50px;
  border: 1px solid #d3d3d3;
  border-radius: 20px;
  box-shadow: 2px 2px 30px 0px #00000040;
}
.divider {
  margin-block: 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: center;

  gap: 10px;

  & div:first-child,
  & div:last-child {
    margin-top: 2px;
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    align-content: center;

    & > span {
      display: inline-block;
      width: 100%;
      height: 2px;
      background-color: #959a95;
    }
  }
}
.small-text {
  text-align: center;
  font-weight: 400;
  font-size: 0.9rem;
  color: #6b6c74;
}
.link {
  font-weight: 500;
  display: inline;
  color: #0a69ff;
  text-decoration: none;
  cursor: pointer;

  &.small {
    text-align: center;
    font-size: 0.9rem;
    font-weight: 400;
  }

  &:hover {
    text-decoration: underline;
  }
}
.rules-list {
  list-style-type: disc;
  padding-left: 25px;

  & > li,
  & > b {
    line-height: 30px;
  }
}
.google-btn {
  font-size: 1rem;
  align-items: center;
  background-color: #e2edff;
  border-radius: 4px;
  display: flex;
  gap: 8px;
  justify-content: center;
  padding-bottom: 7px;
  padding-top: 7px;

  &:focus:not(:active) {
    box-shadow: 0 0 0 0.125em rgba(168, 177, 190, 0.25);
  }
}
.btn-google {
  background-color: #e2edff;
  border-color: #e2edff;
  border-radius: 4px;
  border-width: 1px;
  color: hsl(0, 0%, 21%);
  box-shadow: none;
  display: inline-flex;
  font-size: 1rem;
  height: 2.25em;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  padding-bottom: calc(0.375em - 1px);
  padding-left: 0.75em;
  padding-right: 0.75em;
  padding-top: calc(0.375em - 1px);
  text-align: center;
  white-space: nowrap;
  gap: 8px;
  justify-content: center;

  &:focus:not(:active) {
    box-shadow: 0 0 0 0.125em rgba(229, 240, 255, 0.5);
  }
  &:focus {
    border-color: transparent;
  }
}
</style>
